import { computed, inject, ref, useSlots, watch } from 'vue';
import { useField as useVeeField } from 'vee-validate';
import { extend } from 'lodash-es';
import { useFieldError } from '@phoenix/composables/fieldError';

export const useField = (props, additionalFieldInfo = {}) => {
  const slots = useSlots();

  const getFormGroupField = inject('getFormGroupField');

  const isDirty = ref(false);

  const formGroupField = computed(() => getFormGroupField());

  const hasLabelSlot = computed(() => !!slots.label);

  const field = computed(() => {
    const defaultFieldInfo = {
      ariaLabel: null,
      placeholder: null,
      disabled: false,
      required: false,
      value: '',
      autocomplete: true,
      rules: {},
    };

    if (props.attrs) {
      return extend(defaultFieldInfo, props.attrs, additionalFieldInfo);
    } else if (!('id' in formGroupField.value)) {
      throw new Error('id attribute should be present');
    } else if (!('name' in formGroupField.value)) {
      throw new Error('name attribute should be present');
    } else {
      return extend(defaultFieldInfo, formGroupField.value, additionalFieldInfo);
    }
  });

  const { displayedErrors, isInvalid } = useFieldError(field);

  if (props.shouldValidate) {
    const shouldValidateImmediately = inject('shouldValidateImmediately');

    const veeValidations = computed(() => {
      const rules = {
        ...field.value.rules,
        required: field.value.required,
      };

      if (field.value.length) {
        rules.max = field.value.length;
      }

      if (field.value.type === 'email') {
        rules.email = true;
      }

      return rules;
    });

    const { setValue } = useVeeField(() => field.value.name, veeValidations);

    watch(
      () => props.modelValue,
      (value) => {
        setValue(value);
      },
      { immediate: shouldValidateImmediately },
    );
  }

  watch(
    () => props.modelValue,
    (value) => {
      isDirty.value = !!value;
    },
    { immediate: true },
  );

  return {
    displayedErrors,
    field,
    isInvalid,
    hasLabelSlot,
    isDirty,
  };
};
